import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { GalleryViewer } from '@/components/Gallery';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Star, Copy, ArrowRight, CheckCircle, PenLine } from 'lucide-react';
import { ActivityInfo } from '@/components/ActivityInfo';
import { StarDisplay } from '@/components/StarRating';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESTAURANT_IMAGES = [
  "https://images.unsplash.com/photo-1685040235380-a42a129ade4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc2MzI2Mzk5fDA&ixlib=rb-4.1.0&q=85",
];

export default function RestaurantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const swapReviewId = searchParams.get('swap_review_id');
  const { user, login } = useAuth();
  const { t } = useLang();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);
  const [creatingDraft, setCreatingDraft] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/restaurants/${id}`); setRestaurant(res.data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const handleSelectCode = async (referralCode, reviewId) => {
    if (!user) { login(); return; }
    setCreatingDraft(true);
    try {
      if (swapReviewId) {
        // Phase 20 — swap referral on existing (possibly CORRECTION_REQUIRED) review
        await axios.patch(`${API}/drafts/${swapReviewId}`, {
          linked_referral_code: referralCode,
        }, { withCredentials: true });
        toast.success(t('rd_referral_swapped') || 'Referral aggiornato');
        navigate('/my-reviews');
        return;
      }
      await axios.post(`${API}/drafts`, {
        restaurant_id: restaurant.restaurant_id,
        referral_code: referralCode,
        review_id: reviewId
      }, { withCredentials: true });
      toast.success(t('rd_draft_created'));
      navigate('/my-reviews');
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Phase 20 — inactive referrer
      if (e.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'REFERRAL_USER_INACTIVE') {
        toast.error(t('ir_toast_inactive') || "L'utente scelto non è più attivo su WOMIO. Scegline un altro.");
      } else if (e.response?.status === 409) {
        toast.error(t('rd_already_draft'));
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Error'));
      }
    } finally { setCreatingDraft(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>;
  if (!restaurant) return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-[60vh]"><p className="text-muted-foreground font-sans">Not found</p></div></div>;

  const hasUserReviews = restaurant.reviews?.length > 0;

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="restaurant-detail-page">
      <Navbar />
      <div className="relative h-52 sm:h-72 md:h-96 mt-14 sm:mt-16">
        {(() => {
          const cover = restaurant.gallery?.find(g => g.is_cover) || restaurant.gallery?.[0];
          const src = cover ? `${process.env.REACT_APP_BACKEND_URL}${cover.url_display}` : (restaurant.image_url ? (restaurant.image_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${restaurant.image_url}` : restaurant.image_url) : RESTAURANT_IMAGES[0]);
          return <img src={src} alt={restaurant.name} className="w-full h-full object-cover" data-testid="detail-hero" />;
        })()}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">{t('rest_earn')}</Badge>
            {restaurant.avg_rating > 0 && <StarDisplay value={restaurant.avg_rating} size="sm" />}
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl text-white font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{restaurant.name}</h1>
          <div className="flex items-center gap-3 mt-2 text-white/80 text-xs sm:text-sm font-sans">
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> {restaurant.city || restaurant.address}</span>
            {restaurant.cuisine && <><span className="text-white/40">|</span><span>{restaurant.cuisine}</span></>}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          <div className="lg:col-span-2">
            {/* Gallery */}
            {(restaurant.gallery?.length > 0) && (
              <div className="mb-6 sm:mb-8" data-testid="detail-gallery-section">
                <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('gal_title')}</h2>
                <GalleryViewer gallery={restaurant.gallery} t={t} />
              </div>
            )}
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t('rd_reviews')} ({restaurant.reviews?.length || 0})
            </h2>

            {!hasUserReviews ? (
              <Card className="border border-border rounded-2xl">
                <CardContent className="p-6 sm:p-8 text-center">
                  <Star className="w-10 h-10 text-primary mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="text-lg sm:text-xl font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('rd_be_first')}</h3>
                  <p className="text-muted-foreground font-sans text-sm mb-2">{t('rd_no_reviews')}</p>
                  <p className="text-xs text-muted-foreground font-sans mb-6">
                    {t('rd_founder_code')} <span className="font-mono font-semibold text-primary">{restaurant.founder_referral_code}</span>
                  </p>
                  <Button data-testid="write-first-review-btn" onClick={() => handleSelectCode(restaurant.founder_referral_code, null)} disabled={creatingDraft} className="rounded-full btn-lift">
                    <PenLine className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('rd_write_review')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {restaurant.reviews.map((review, i) => (
                  <Card key={review.review_id} className="border border-border rounded-2xl card-hover" data-testid={`review-card-${i}`}>
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {review.reviewer_picture ? <img src={review.reviewer_picture} alt="" className="w-8 sm:w-10 h-8 sm:h-10 rounded-full object-cover" />
                            : <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs sm:text-sm">{review.reviewer_name?.[0]}</div>}
                          <div>
                            <p className="font-semibold text-sm font-sans">{review.reviewer_name}</p>
                            <p className="text-xs text-muted-foreground font-sans">{new Date(review.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] sm:text-xs"><CheckCircle className="w-3 h-3 mr-1" /> {t('rd_verified')}</Badge>
                      </div>
                      {review.rating > 0 && <div className="mb-2"><StarDisplay value={review.rating} size="sm" /></div>}
                      <p className="text-sm text-foreground leading-relaxed mb-4 font-sans">{review.text}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
                          <span className="text-xs text-muted-foreground font-sans font-mono">{review.referral_code}</span>
                          <button onClick={() => copyCode(review.referral_code)} className="text-muted-foreground hover:text-primary transition-colors" data-testid={`copy-code-${i}`}>
                            {copiedCode === review.referral_code ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        <Button data-testid={`use-code-btn-${i}`} size="sm" onClick={() => handleSelectCode(review.referral_code, review.review_id)} disabled={creatingDraft} className="rounded-full text-xs btn-lift">
                          {t('rd_use_code')} <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4 sm:space-y-6">
              <Card className="border border-border rounded-2xl" data-testid="restaurant-sidebar">
                <CardContent className="p-5 sm:p-6">
                  <h3 className="font-semibold text-sm font-sans mb-3">{t('rd_about')}</h3>
                  <p className="text-sm text-muted-foreground font-sans mb-3">{restaurant.description || t('rd_no_reviews')}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground font-sans"><MapPin className="w-4 h-4" strokeWidth={1.5} /><span data-testid="detail-address">{restaurant.address}</span></div>
                  <ActivityInfo restaurant={restaurant} t={t} />
                </CardContent>
              </Card>
              <Card className="border border-primary/20 rounded-2xl bg-primary/5" data-testid="write-review-cta">
                <CardContent className="p-5 sm:p-6 text-center">
                  <Star className="w-8 h-8 text-primary mx-auto mb-3" strokeWidth={1.5} />
                  <h3 className="font-semibold text-sm font-sans mb-2">{t('rd_been_here')}</h3>
                  <p className="text-xs text-muted-foreground font-sans mb-4">{t('rd_been_here_d')}</p>
                  <Button data-testid="sidebar-write-review-btn"
                    onClick={() => handleSelectCode(hasUserReviews ? restaurant.reviews[0].referral_code : restaurant.founder_referral_code, hasUserReviews ? restaurant.reviews[0].review_id : null)}
                    disabled={creatingDraft} className="rounded-full w-full btn-lift">
                    <PenLine className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('rd_write_review')}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
